'use client';

import { ErrorSection } from '@pickleballinc/react-ui';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';

import { usePlayerTournaments } from '@/hooks/players/useGetPlayerTournaments';
import { FilterTabs } from '@/modules/players/v2/navigation/FilterTabs';
import { EventCardsListSkeleton } from '@/modules/players/v2/navigation/skeletons/EventCardsListSkeleton';
import { PlayerTournamentsWrapper } from '@/modules/players/v2/tabs/tournaments/PlayerTournamentsWrapper';
import { PlayerData, PlayerParticipationType } from '@/types/players.types';
import { TournamentStatus } from '@/types/playerTournaments.types';
import { GetPBApproveToCreatePermissionRes } from '@/utils/actions/leagues/getPBApproveToCreatePermission';

interface TournamentClientWrapperProps {
  player: PlayerData;
  params: { slug: string };
  activeTournamentType: PlayerParticipationType;
  tournamentStatus: TournamentStatus;
  searchParams: {
    [key: string]: string;
  };
  createPerms: GetPBApproveToCreatePermissionRes;
}

export const TournamentClientWrapper = ({
  player,
  activeTournamentType,
  tournamentStatus,
  searchParams,
  createPerms,
}: TournamentClientWrapperProps) => {
  const { session } = useSession();

  const {
    data: torunamentResult,
    isLoading,
    isFetching,
  } = usePlayerTournaments({
    playerId: player.uuid,
    playerPbUuid: player.pbUuid,
    activeTournamentType,
    tournamentStatus,
    searchParams,
  });

  if (!isLoading && torunamentResult?.error) {
    return (
      <ErrorSection
        Subheading='Please reach out to our support to report this issue'
        heading='Something wrong happened'
      />
    );
  }

  const hasTournaments = torunamentResult ? torunamentResult.totalRecords > 0 : false;

  return (
    <>
      <FilterTabs hasData={hasTournaments} type='tournament' />

      <div className='container mx-auto mb-8 !max-w-[996px] rounded-md xl:!px-0'>
        {isLoading || isFetching ? (
          <EventCardsListSkeleton />
        ) : (
          <PlayerTournamentsWrapper
            player={player}
            currentData={torunamentResult?.data ?? []}
            user={session?.user}
            totalRecords={torunamentResult?.totalRecords ?? 0}
            createPerms={createPerms}
          />
        )}
      </div>
    </>
  );
};
